import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, MapPin, Phone, CalendarDays } from "lucide-react";

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, passed: false });

  useEffect(() => {
    const target = new Date(targetDate).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, passed: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        passed: false,
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

export default function Hero() {
  const countdown = useCountdown("2026-04-09T10:00:00+01:00");
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center bg-gradient-to-br from-stone-50 via-white to-green-50/30 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-green-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-orange-50/50 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text */}
          <div className="animate-fade-up">
            <Badge
              className="bg-green-100 text-green-800 border-0 mb-6 px-4 py-1.5 rounded-full text-sm font-medium"
              data-testid="hero-badge"
            >
              Trusted Healthcare in Ibadan
            </Badge>

            {/* Grand Opening Countdown */}
            {!countdown.passed ? (
              <div className="mb-6 bg-gradient-to-r from-green-700 to-green-800 rounded-2xl p-5 text-white shadow-lg" data-testid="countdown-banner">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="w-5 h-5 text-green-200" />
                  <span className="font-[Outfit] font-semibold text-sm sm:text-base tracking-wide uppercase">Grand Opening — April 9th, 2026 at 10AM</span>
                </div>
                <div className="flex gap-3 sm:gap-4">
                  {[
                    { value: countdown.days, label: "Days" },
                    { value: countdown.hours, label: "Hours" },
                    { value: countdown.minutes, label: "Mins" },
                    { value: countdown.seconds, label: "Secs" },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col items-center bg-white/15 backdrop-blur-sm rounded-xl px-3 sm:px-5 py-2.5 min-w-[56px]">
                      <span className="font-[Outfit] text-2xl sm:text-3xl font-bold tabular-nums">{String(item.value).padStart(2, "0")}</span>
                      <span className="text-[10px] sm:text-xs text-green-200 uppercase tracking-wider font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-green-200 text-xs mt-3">We can't wait to welcome you. Book your appointment now!</p>
              </div>
            ) : (
              <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-4 text-white shadow-lg" data-testid="now-open-banner">
                <p className="font-[Outfit] font-bold text-lg">We Are Now Open!</p>
                <p className="text-orange-100 text-sm">Walk in or book an appointment today.</p>
              </div>
            )}

            <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-[1.1] mb-6">
              Your Health,{" "}
              <span className="text-green-700">Our Priority</span>
            </h1>

            <p className="text-base md:text-lg text-stone-600 leading-relaxed max-w-lg mb-8">
              IV Medics and Diagnostics Clinic provides expert medical care, infection treatment, surgical procedures, and comprehensive diagnostics including ultrasound and ECG — all under one roof.
            </p>

            {/* Quick Info Pills */}
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
                <Clock className="w-4 h-4 text-green-700" />
                Open 24 Hours, 7 Days
              </div>
              <div className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm">
                <MapPin className="w-4 h-4 text-green-700" />
                Bodija, Ibadan
              </div>
              <a
                href="tel:09162731000"
                className="flex items-center gap-2 bg-white border border-stone-100 rounded-full px-4 py-2 text-sm text-stone-600 shadow-sm hover:border-green-200 transition-colors"
              >
                <Phone className="w-4 h-4 text-green-700" />
                09162731000
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/book">
                <Button
                  data-testid="hero-book-btn"
                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all gap-2"
                >
                  Book Appointment <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Button
                data-testid="hero-services-btn"
                variant="outline"
                onClick={() =>
                  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
                }
                className="border-stone-200 text-stone-700 hover:bg-stone-50 rounded-full px-8 py-6 text-base"
              >
                Our Services
              </Button>
            </div>
          </div>

          {/* Right - Image */}
          <div className="animate-fade-up animate-delay-200 hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-green-100/30 rounded-[2rem] rotate-3 scale-105" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/c9f675ad-548f-4c2a-9226-1cdbef424c9a/images/0bd13c53bfb4af692a89a4b651e7d60dfdedfa94ef08785c259a36dfd6b3ff9d.png"
                alt="IV Medics - Nigerian Medical Team"
                className="relative rounded-[2rem] w-full h-[500px] object-cover shadow-2xl"
                data-testid="hero-image"
              />
              {/* Floating card */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-stone-100 animate-fade-up animate-delay-400">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-[Outfit] font-semibold text-stone-900 text-sm">142+ Services Available</p>
                    <p className="text-xs text-stone-500">Walk-in & same-day results</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
